import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate, fmtGNF } from "@/lib/format";
import { ABtn, AInput, ASelect, Panel } from "./AdminLayout";

export function AdminCustomers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");

  const load = () =>
    api
      .get("/admin/customers", { params: { search: search || undefined } })
      .then((r) => setUsers(r.data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const update = async (u, payload) => {
    try {
      await api.put(`/admin/customers/${u.user_id}`, payload);
      toast.success("Utilisateur mis à jour");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Panel
      title="Clients et utilisateurs"
      subtitle={`${users.length} compte(s)`}
      actions={<AInput placeholder="Nom, email, téléphone…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" data-testid="admin-customer-search" />}
      testid="admin-customers-page"
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-400">
            <tr className="border-b border-slate-800">
              <th className="py-2">Client</th>
              <th className="py-2">Téléphone</th>
              <th className="py-2">Rôle</th>
              <th className="py-2 text-right">Commandes</th>
              <th className="py-2 text-right">Total dépensé</th>
              <th className="py-2">Inscription</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-b border-slate-800/60" data-testid={`admin-customer-row-${u.email}`}>
                <td className="py-2">
                  <p className="font-medium text-white">{u.name}</p>
                  <p className="text-[11px] text-slate-400">{u.email}</p>
                </td>
                <td className="font-num py-2 text-slate-300">{u.phone || "—"}</td>
                <td className="py-2">
                  <ASelect
                    value={u.role}
                    onChange={(e) => update(u, { role: e.target.value })}
                    className="w-36"
                    data-testid={`admin-customer-role-${u.email}`}
                  >
                    {["client", "vendor", "warehouse", "admin"].map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </ASelect>
                </td>
                <td className="font-num py-2 text-right text-slate-300">{u.orders_count}</td>
                <td className="font-num py-2 text-right text-emerald-400">{fmtGNF(u.total_spent)}</td>
                <td className="py-2 text-[11px] text-slate-400">{fmtDate(u.created_at)}</td>
                <td className="py-2 text-right">
                  <ABtn
                    variant={u.is_active ? "danger" : "ghost"}
                    onClick={() => update(u, { is_active: !u.is_active })}
                    data-testid={`admin-customer-toggle-${u.email}`}
                  >
                    {u.is_active ? "Désactiver" : "Réactiver"}
                  </ABtn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

export function AdminLogs() {
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    api.get("/admin/logs").then((r) => setLogs(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  return (
    <Panel title="Journal des opérations" subtitle="100 dernières actions sensibles" testid="admin-logs-page">
      <div className="space-y-1.5">
        {logs.length === 0 && <p className="text-sm text-slate-500">Aucune opération enregistrée.</p>}
        {logs.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 px-3 py-2 text-xs" data-testid={`log-row-${l.id}`}>
            <span className="font-num text-slate-500">{fmtDate(l.created_at)}</span>
            <span className="font-semibold text-white">{l.action}</span>
            <span className="text-slate-400">{l.user_email} ({l.role})</span>
            <span className="font-num text-slate-500">{l.target}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}
